/**
 * Trace Detail page component.
 * Shows all log events for a specific trace across all components.
 *
 * @module pages/TraceDetailPage
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Check,
  AlertTriangle,
  AlertCircle,
  Info,
  Bug,
  ChevronDown,
  ChevronRight,
  Clock,
  Server,
  Globe,
  Cpu,
  Filter,
  X,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { LoadingPage, Badge, Button, Card, CardBody } from '@/components/ui';
import { ApiException } from '@/services/httpClient';
import { useTraceEvents } from '@/hooks';
import type { LogEvent, LogsTraceEventsResponse } from '@/types';

type ComponentFilter = 'all' | 'globalApi' | 'clusterApi' | 'nodeAgent';
type LogLevelFilter = 'all' | 'Error' | 'Warning' | 'Information' | 'Debug';

/**
 * Get variant for log level badge
 */
function getLogLevelVariant(logLevel: string): 'danger' | 'warning' | 'info' | 'neutral' {
  switch (logLevel.toLowerCase()) {
    case 'error':
    case 'critical':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'information':
    case 'info':
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * Get icon for log level
 */
function getLogLevelIcon(logLevel: string) {
  switch (logLevel.toLowerCase()) {
    case 'error':
    case 'critical':
      return <AlertCircle className="h-3.5 w-3.5" />;
    case 'warning':
      return <AlertTriangle className="h-3.5 w-3.5" />;
    case 'information':
    case 'info':
      return <Info className="h-3.5 w-3.5" />;
    default:
      return <Bug className="h-3.5 w-3.5" />;
  }
}

/**
 * Get component display info
 */
function getComponentInfo(sourceTable: string): {
  name: string;
  icon: React.ReactNode;
  color: string;
} {
  if (sourceTable.includes('Global')) {
    return {
      name: 'Global API',
      icon: <Globe className="h-3.5 w-3.5" />,
      color: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30',
    };
  }
  if (sourceTable.includes('Node')) {
    return {
      name: 'Node Agent',
      icon: <Cpu className="h-3.5 w-3.5" />,
      color: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
    };
  }
  return {
    name: 'Cluster API',
    icon: <Server className="h-3.5 w-3.5" />,
    color: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
  };
}

/**
 * Merge and sort all events from different sources
 */
function mergeEvents(data: LogsTraceEventsResponse): (LogEvent & { source: string })[] {
  const allEvents: (LogEvent & { source: string })[] = [
    ...data.globalApiEvents.map((e) => ({ ...e, source: 'globalApi' })),
    ...data.clusterApiEvents.map((e) => ({ ...e, source: 'clusterApi' })),
    ...data.nodeAgentEvents.map((e) => ({ ...e, source: 'nodeAgent' })),
  ];

  // Sort by timestamp
  return allEvents.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

/**
 * Trace detail page
 */
export function TraceDetailPage() {
  const { traceId } = useParams<{ traceId: string }>();
  const navigate = useNavigate();
  const decodedTraceId = traceId ? decodeURIComponent(traceId) : '';
  const { data, isLoading, error } = useTraceEvents(decodedTraceId);

  const [copiedTraceId, setCopiedTraceId] = useState(false);
  const [componentFilter, setComponentFilter] = useState<ComponentFilter>('all');
  const [logLevelFilter, setLogLevelFilter] = useState<LogLevelFilter>('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Merge and filter events
  const allEvents = useMemo(() => {
    if (!data) return [];
    return mergeEvents(data);
  }, [data]);

  const filteredEvents = useMemo(() => {
    let events = allEvents;

    // Filter by component
    if (componentFilter !== 'all') {
      events = events.filter((e) => e.source === componentFilter);
    }

    // Filter by log level
    if (logLevelFilter !== 'all') {
      events = events.filter((e) => e.logLevel.toLowerCase() === logLevelFilter.toLowerCase());
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      events = events.filter(
        (e) =>
          e.message?.toLowerCase().includes(query) ||
          e.operation?.toLowerCase().includes(query) ||
          e.sandboxId?.toLowerCase().includes(query) ||
          e.diskImageId?.toLowerCase().includes(query) ||
          e.eventName?.toLowerCase().includes(query)
      );
    }

    return events;
  }, [allEvents, componentFilter, logLevelFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: allEvents.length,
      errors: allEvents.filter((e) => e.logLevel.toLowerCase() === 'error').length,
      warnings: allEvents.filter((e) => e.logLevel.toLowerCase() === 'warning').length,
      globalApi: allEvents.filter((e) => e.source === 'globalApi').length,
      clusterApi: allEvents.filter((e) => e.source === 'clusterApi').length,
      nodeAgent: allEvents.filter((e) => e.source === 'nodeAgent').length,
    };
  }, [allEvents]);

  const handleCopyTraceId = () => {
    if (decodedTraceId) {
      navigator.clipboard.writeText(decodedTraceId);
      setCopiedTraceId(true);
      setTimeout(() => setCopiedTraceId(false), 2000);
    }
  };

  const toggleEventExpanded = (index: number) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedEvents(newExpanded);
  };

  const handleExpandAll = () => {
    setExpandedEvents(new Set(filteredEvents.map((_, i) => i)));
  };

  const handleCollapseAll = () => {
    setExpandedEvents(new Set());
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    const isNotFound = error instanceof ApiException && error.status === 404;

    return (
      <div className="p-6">
        <button
          onClick={() => navigate('/logs')}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Debug Logs
        </button>

        {isNotFound ? (
          <div className="bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg p-6 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Trace not found
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
              The trace ID{' '}
              <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-xs">
                {decodedTraceId}
              </code>{' '}
              was not found. This could mean the trace doesn&apos;t exist, has expired, or belongs
              to another user.
            </p>
            <Button onClick={() => navigate('/logs')} variant="secondary">
              Try Another Trace ID
            </Button>
          </div>
        ) : (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-900 dark:text-red-200 mb-2">
              Failed to load trace
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => navigate('/logs')} variant="secondary">
              Go Back
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="p-6">
      {/* Back button */}
      <Link
        to="/logs"
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to traces
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Trace Details</h1>
          <div className="mt-2 flex items-center gap-2">
            <code className="text-sm font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#161616] px-2 py-1 rounded">
              {decodedTraceId}
            </code>
            <button
              onClick={handleCopyTraceId}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              title="Copy trace ID"
            >
              {copiedTraceId ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="flex flex-wrap gap-2">
          <div className="px-3 py-2 bg-gray-100 dark:bg-[#161616] rounded-lg">
            <div className="text-xs text-gray-500 dark:text-gray-400">Total Events</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">{stats.total}</div>
          </div>
          {stats.errors > 0 && (
            <div className="px-3 py-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <div className="text-xs text-red-600 dark:text-red-400">Errors</div>
              <div className="text-lg font-semibold text-red-700 dark:text-red-300">
                {stats.errors}
              </div>
            </div>
          )}
          {stats.warnings > 0 && (
            <div className="px-3 py-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <div className="text-xs text-yellow-600 dark:text-yellow-400">Warnings</div>
              <div className="text-lg font-semibold text-yellow-700 dark:text-yellow-300">
                {stats.warnings}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages, operations, IDs..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white dark:placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Component filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Component:</span>
          <select
            value={componentFilter}
            onChange={(e) => setComponentFilter(e.target.value as ComponentFilter)}
            className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All ({stats.total})</option>
            <option value="globalApi">Global API ({stats.globalApi})</option>
            <option value="clusterApi">Cluster API ({stats.clusterApi})</option>
            <option value="nodeAgent">Node Agent ({stats.nodeAgent})</option>
          </select>
        </div>

        {/* Log level filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Level:</span>
          <select
            value={logLevelFilter}
            onChange={(e) => setLogLevelFilter(e.target.value as LogLevelFilter)}
            className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All</option>
            <option value="Error">Errors</option>
            <option value="Warning">Warnings</option>
            <option value="Information">Info</option>
            <option value="Debug">Debug</option>
          </select>
        </div>

        {/* Expand/Collapse All */}
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExpandAll} className="!px-3 !py-1.5 text-sm">
            Expand All
          </Button>
          <Button variant="secondary" onClick={handleCollapseAll} className="!px-3 !py-1.5 text-sm">
            Collapse All
          </Button>
        </div>
      </div>

      {/* Events timeline */}
      <div className="space-y-2">
        {filteredEvents.length === 0 ? (
          <Card>
            <CardBody className="py-12 text-center">
              <Info className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">No events found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Try adjusting your filters or search query.
              </p>
            </CardBody>
          </Card>
        ) : (
          filteredEvents.map((event, index) => (
            <EventCard
              key={index}
              event={event}
              isExpanded={expandedEvents.has(index)}
              onToggle={() => toggleEventExpanded(index)}
              previousTimestamp={index > 0 ? filteredEvents[index - 1].timestamp : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Individual event card component
 */
function EventCard({
  event,
  isExpanded,
  onToggle,
  previousTimestamp,
}: {
  event: LogEvent & { source: string };
  isExpanded: boolean;
  onToggle: () => void;
  previousTimestamp?: string;
}) {
  const componentInfo = getComponentInfo(event.sourceTable);
  const timestamp = new Date(event.timestamp);

  // Calculate time delta from previous event
  const timeDelta = previousTimestamp
    ? new Date(event.timestamp).getTime() - new Date(previousTimestamp).getTime()
    : 0;

  const isError = event.logLevel.toLowerCase() === 'error';
  const isWarning = event.logLevel.toLowerCase() === 'warning';

  // Parse value JSON if present
  let parsedValue: Record<string, unknown> | null = null;
  if (event.value) {
    try {
      parsedValue = JSON.parse(event.value);
    } catch {
      // Value is not valid JSON, keep as string
    }
  }

  return (
    <div
      className={clsx(
        'border rounded-lg overflow-hidden',
        isError && 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10',
        isWarning &&
          'border-yellow-300 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-900/10',
        !isError && !isWarning && 'border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]'
      )}
    >
      {/* Header - always visible */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 dark:hover:bg-[#161616] transition-colors"
      >
        {/* Expand/collapse indicator */}
        <div className="mt-0.5 text-gray-400">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>

        {/* Timestamp */}
        <div className="flex-shrink-0 w-24">
          <div className="text-xs font-mono text-gray-600 dark:text-gray-400">
            {format(timestamp, 'HH:mm:ss.SSS')}
          </div>
          {timeDelta > 0 && (
            <div className="text-xs text-gray-400 dark:text-gray-500">
              +{timeDelta < 1000 ? `${timeDelta}ms` : `${(timeDelta / 1000).toFixed(1)}s`}
            </div>
          )}
        </div>

        {/* Log level */}
        <Badge variant={getLogLevelVariant(event.logLevel)} className="flex-shrink-0">
          <span className="flex items-center gap-1">
            {getLogLevelIcon(event.logLevel)}
            {event.logLevel}
          </span>
        </Badge>

        {/* Component */}
        <span
          className={clsx(
            'flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
            componentInfo.color
          )}
        >
          {componentInfo.icon}
          {componentInfo.name}
        </span>

        {/* Message preview */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
            {event.message || event.operation || event.eventName || 'No message'}
          </p>
          {event.elapsedMs !== undefined && event.elapsedMs > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
              <Clock className="h-3 w-3" />
              {event.elapsedMs.toFixed(1)}ms
            </span>
          )}
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-[#2a2a2a]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Basic info */}
            {event.operation && <DetailItem label="Operation" value={event.operation} />}
            {event.eventName && <DetailItem label="Event Name" value={event.eventName} />}
            {event.eventId && <DetailItem label="Event ID" value={String(event.eventId)} />}
            {typeof event.elapsedMs === 'number' && (
              <DetailItem label="Duration" value={`${event.elapsedMs.toFixed(2)}ms`} />
            )}
            {event.region && <DetailItem label="Region" value={event.region} />}
            {event.buildVersion && (
              <DetailItem label="Build Version" value={event.buildVersion} code />
            )}

            {/* Entity IDs */}
            {event.sandboxId && (
              <DetailItem label="Sandbox ID" value={event.sandboxId} code copyable />
            )}
            {event.instanceId && (
              <DetailItem label="Instance ID" value={event.instanceId} code copyable />
            )}
            {event.diskImageId && (
              <DetailItem label="Disk Image ID" value={event.diskImageId} code copyable />
            )}
          </div>

          {/* Full message */}
          {event.message && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Message
              </div>
              <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#161616] p-3 rounded">
                {event.message}
              </p>
            </div>
          )}

          {/* Exception info */}
          {(event.exceptionType || event.exceptionMessage) && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              <div className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">
                Exception
              </div>
              {event.exceptionType && (
                <p className="text-sm font-mono text-red-700 dark:text-red-300">
                  {event.exceptionType}
                </p>
              )}
              {event.exceptionMessage && (
                <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                  {event.exceptionMessage}
                </p>
              )}
            </div>
          )}

          {/* Value (additional data) */}
          {parsedValue && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Additional Data
              </div>
              <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-[#161616] p-3 rounded overflow-x-auto">
                {JSON.stringify(parsedValue, null, 2)}
              </pre>
            </div>
          )}
          {event.value && !parsedValue && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Value</div>
              <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#161616] p-3 rounded">
                {event.value}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Detail item component for displaying key-value pairs
 */
function DetailItem({
  label,
  value,
  code,
  copyable,
}: {
  label: string;
  value: string;
  code?: boolean;
  copyable?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="flex items-center gap-1">
        {code ? (
          <code className="text-sm font-mono text-gray-900 dark:text-gray-100">{value}</code>
        ) : (
          <span className="text-sm text-gray-900 dark:text-gray-100">{value}</span>
        )}
        {copyable && (
          <button
            onClick={handleCopy}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
            title="Copy"
          >
            {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
          </button>
        )}
      </div>
    </div>
  );
}
